import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import type {Notification, NotificationPage, AdminNotificationPage, NotificationWithData} from '@/types/notification'
import {Services} from "@/services/types.ts";
import {UUID} from "@/types/common.ts";

 

export function useUnreadCount() {
    const services = useServices()
    return useQuery<number>({
        queryKey: ['notifications', 'unread-count'],
        queryFn: async () => {
            const data = await services.notifications.unreadCount()
            return data.count
        },
        refetchInterval: 30_000,
    })
}

function loadNotificationData(n: Notification, services: Services): Promise<NotificationWithData<unknown>> {
    if (n?.referenceId != null && n.eventType?.startsWith('ACCESS_REQUEST_')) {
        return services.accessRequests.detail(n.referenceId as UUID)
            .then(r => ({data: r, ...n}))
            .catch(() => ({data: null, ...n} as NotificationWithData<null>));
    } else {
        return Promise.resolve({data: null, ...n} as NotificationWithData<null>);
    }
}

export function useNotification(id: string | undefined) {
    const services = useServices()
    return useQuery<Notification>({
        queryKey: ['notifications', 'detail', id],
        queryFn: () => services.notifications.detail(id!)
            .then((n: Notification) => loadNotificationData(n, services)),
        enabled: !!id,
    })
}

export function useNotifications(queryString: string) {
    const services = useServices()
    return useQuery<NotificationPage>({
        queryKey: ['notifications', queryString],
        queryFn: () => services.notifications.list(Object.fromEntries(new URLSearchParams(queryString))),
    })
}

export function useRecentNotifications(enabled: boolean) {
    const services = useServices()
    return useQuery<NotificationPage>({
        queryKey: ['notifications', 'recent'],
        queryFn: () => services.notifications.list({size: 5, sort: 'createdAt,desc'}),
        enabled,
    })
}

export function useDashboardNotifications() {
    const services = useServices()
    return useQuery<NotificationPage>({
        queryKey: ['notifications', 'dashboard'],
        queryFn: () => services.notifications.list({size: 5}),
        refetchInterval: 30_000,
    })
}

export function useMarkRead() {
    const services = useServices()
    const queryClient = useQueryClient()
    return useMutation<void, Error, string>({
        mutationFn: (id) => services.notifications.markRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['notifications']})
        },
    })
}

export function useMarkAllRead() {
    const services = useServices()
    const queryClient = useQueryClient()
    return useMutation<void, Error, void>({
        mutationFn: () => services.notifications.markAllRead(),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['notifications']})
        },
    })
}

export function useAdminNotificationLog(queryString: string) {
    const services = useServices()
    return useQuery<AdminNotificationPage>({
        queryKey: ['notifications', 'log', queryString],
        queryFn: () => services.notifications.adminLog(Object.fromEntries(new URLSearchParams(queryString))),
    })
}
